// Suggest a drawing's scale from the scale note printed on it.
//
// SUGGEST ONLY. A printed note can describe a detail rather than the sheet,
// and a sheet can carry several scales, so nothing here ever sets a scale: the
// estimator confirms a suggestion (or picks / calibrates another). Everything is
// pure — callers feed it text items read from the PDF text layer.
//
// ftPerInch = real FEET represented by one inch of paper, the unit the sheet's
// ImageFtPerInch is stored in (1/8" = 1'-0"  ->  1 in paper = 8 ft  ->  8).

import { toIndexedLines } from "./textLines";

const MIN_FT_PER_INCH = 0.02;   // 1/0.02 -> 600:1 — anything finer is a misread
const MAX_FT_PER_INCH = 1200;   // 1" = 1200'

const QUOTE = `["”″']{1,2}`;       // inch mark (also the OCR'd double-prime / two single quotes)
const FOOT = `['’′]`;

// paper side: "1 1/2", "1-1/2", "3/32", "1", "0.5"
const PAPER = `(?:(\\d+)[\\s-]+)?(\\d+)\\s*/\\s*(\\d+)|(\\d+(?:\\.\\d+)?)`;
// real side: 1'-0"  /  20'  /  20'-6"  /  1'
const REAL = `(\\d+(?:\\.\\d+)?)\\s*${FOOT}\\s*(?:-?\\s*(\\d+(?:\\.\\d+)?)\\s*(?:${QUOTE})?)?`;
const ARCH_RE = new RegExp(`(?:${PAPER})\\s*(?:${QUOTE})?\\s*=\\s*${REAL}`, "g");
// ratio notes only count with the word SCALE beside them ("SCALE 1:100", "SCALE: 1 : 50")
const RATIO_RE = /SCALE\s*[:\-]?\s*1\s*:\s*(\d+(?:\.\d+)?)/gi;

function paperInches(m) {
  // m[1] whole part of a mixed number, m[2]/m[3] fraction, m[4] plain number
  if (m[4] != null) return parseFloat(m[4]);
  const whole = m[1] != null ? parseInt(m[1], 10) : 0;
  const den = parseInt(m[3], 10);
  if (!den) return NaN;
  return whole + parseInt(m[2], 10) / den;
}

/** Scale notes found in one string -> [{ ftPerInch, text }] (unvalidated duplicates kept). */
export function parseScaleNotes(str) {
  const out = [];
  if (!str) return out;
  const s = String(str);
  for (const m of s.matchAll(ARCH_RE)) {
    const paper = paperInches(m);
    const feet = parseFloat(m[5]);
    const inches = m[6] != null ? parseFloat(m[6]) : 0;
    const real = feet + inches / 12;
    if (!(paper > 0) || !(real > 0)) continue;
    const ftPerInch = real / paper;
    if (ftPerInch < MIN_FT_PER_INCH || ftPerInch > MAX_FT_PER_INCH) continue;
    out.push({ ftPerInch, text: m[0].replace(/\s+/g, " ").trim() });
  }
  for (const m of s.matchAll(RATIO_RE)) {
    const n = parseFloat(m[1]);
    if (!(n > 0)) continue;
    const ftPerInch = n / 12; // 1 paper inch = n real inches = n/12 ft (unit-agnostic ratio)
    if (ftPerInch < MIN_FT_PER_INCH || ftPerInch > MAX_FT_PER_INCH) continue;
    out.push({ ftPerInch, text: m[0].replace(/\s+/g, " ").trim() });
  }
  return out;
}

const sameScale = (a, b) => Math.abs(a - b) / Math.max(a, b) < 0.005;

/**
 * Detect scale notes on a page.
 *   items — text items [{ str, x, y }] in PDF page units (origin bottom-left)
 *   page  — { width, height } in the same units
 * Returns { candidates, suggestion, ambiguous }:
 *   candidates — distinct scales, title-block ones first: [{ ftPerInch, text, count, inTitleBlock }]
 *   suggestion — the one scale to offer, or null when it is not clear-cut
 *   ambiguous  — several different scales and none is clearly the sheet's
 * The title block is taken as the right-hand strip or the bottom strip of the
 * sheet, where the sheet scale is printed; detail scales are usually elsewhere.
 */
export function detectScaleNotes(items, page) {
  const lines = toIndexedLines(items);
  const w = page?.width || 0, h = page?.height || 0;
  const inTB = (ln) => (w > 0 && ln.x > 0.65 * w) || (h > 0 && ln.y < 0.2 * h);
  const found = [];
  for (const ln of lines) {
    for (const n of parseScaleNotes(ln.text)) found.push({ ...n, inTitleBlock: inTB(ln) });
  }
  const candidates = [];
  for (const f of found) {
    const hit = candidates.find(c => sameScale(c.ftPerInch, f.ftPerInch));
    if (hit) { hit.count += 1; hit.inTitleBlock = hit.inTitleBlock || f.inTitleBlock; }
    else candidates.push({ ftPerInch: f.ftPerInch, text: f.text, count: 1, inTitleBlock: f.inTitleBlock });
  }
  candidates.sort((a, b) => (b.inTitleBlock - a.inTitleBlock) || (b.count - a.count));
  const titleBlock = candidates.filter(c => c.inTitleBlock);
  let suggestion = null;
  if (titleBlock.length === 1) suggestion = titleBlock[0];
  else if (titleBlock.length === 0 && candidates.length === 1) suggestion = candidates[0];
  return { candidates, suggestion, ambiguous: !suggestion && candidates.length > 1 };
}

// Engineering (civil / site) scales: 1" = N'. Architectural scales live in pdfRender.
export const ENGINEERING_SCALES = [10, 20, 30, 40, 50, 60, 100].map(n => ({ label: `1" = ${n}'`, ftPerInch: n }));

/** Does this drawing sheet have an image but no scale yet? (Then measuring is refused.) */
export function sheetNeedsScale(sheet) {
  if (!sheet || !sheet.BackgroundImage) return false; // a blank canvas is in native feet
  return sheet.ImageIsPdf ? !(sheet.ImageFtPerInch > 0) : !(sheet.ImageScaleFtPerPx > 0);
}

export const SCALE_REQUIRED_MESSAGE =
  "Set this drawing's scale first — pick a Scale, accept the plan's printed scale, or use Verify scale. Takeoffs measured on an unscaled drawing would be wrong.";
